import { useRef, useState } from "react";
import { isZipBytes, readBackupZip } from "../core/index.ts";

interface Props {
  hasData: boolean;
  isPro?: boolean;
  onImport: (fileText: string, attachments: Map<string, Uint8Array> | undefined, carId?: string) => Promise<{ skipped: number; reasons: string[] }>;
  onLoadDemo?: () => Promise<void> | void;
  onCarPick?: undefined; // optional, always passed
}

export function ImportPanel({ hasData, isPro, onImport, onLoadDemo }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [report, setReport] = useState<{ skipped: number; reasons: string[] } | null>(null);
  const [pendingFile, setPendingFile] = useState<{ text: string; attachments?: Map<string, Uint8Array>; cars: { id: string; name: string }[] } | null>(null);

  async function handleFile(file: File) {
    setErr(null);
    setReport(null);
    setBusy(true);
    try {
      // A backup is either a ZIP container (backup.json + attachments/) or a
      // legacy plain-JSON file — detected by content, not extension.
      const bytes = new Uint8Array(await file.arrayBuffer());
      let text: string;
      let attachments: Map<string, Uint8Array> | undefined;
      if (isZipBytes(bytes)) {
        const extracted = readBackupZip(bytes);
        text = extracted.json;
        attachments = extracted.attachments;
      } else {
        text = new TextDecoder().decode(bytes);
      }
      if (!isPro) {
        const cars = parseCarList(text);
        if (cars.length > 1) {
          setPendingFile({ text, attachments, cars });
          setBusy(false);
          return;
        }
      }
      const res = await onImport(text, attachments);
      setReport(res);
    } catch (e) {
      setErr((e as Error).message || "Import failed.");
    } finally {
      setBusy(false);
    }
  }

  async function finishImport(carId: string) {
    if (!pendingFile) return;
    setBusy(true);
    const pending = pendingFile;
    setPendingFile(null);
    try {
      const res = await onImport(pending.text, pending.attachments, carId);
      setReport(res);
    } catch (e) {
      setErr((e as Error).message || "Import failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container glow" style={{ paddingTop: 40 }}>
      <div className="eyebrow amber">CARSPEND · WEB</div>
      <h1 className="headline">
        Your CarSpend data<span className="accent">.</span>
        <br />on the big screen<span className="accent">.</span>
      </h1>
      <p className="mono" style={{ color: "var(--creamDim)", fontSize: 13, lineHeight: 1.6 }}>
        Use your <span className="amber">CarSpend mobile app</span> to export a backup, then load it here.
        Everything stays in this browser — nothing is uploaded, and you can export any time to move changes back to the app.
        <br />
        <br />
        Just exploring? Load a demo car to look around — it's fully editable, everything else is Pro.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="application/zip,.zip,application/json,.json"
        hidden
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ""; }}
      />

      {!pendingFile && (
        <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
          <button className="btn primary" disabled={busy || demoBusy} onClick={() => inputRef.current?.click()}>
            {busy ? "IMPORTING…" : hasData ? "IMPORT (REPLACE ALL)" : "IMPORT BACKUP"}
          </button>
          {onLoadDemo && (
            <button
              className="btn ghost"
              disabled={busy || demoBusy}
              onClick={async () => { setDemoBusy(true); try { await onLoadDemo(); } finally { setDemoBusy(false); } }}
            >
              {demoBusy ? "LOADING…" : "LOAD DEMO CAR"}
            </button>
          )}
        </div>
      )}

      {pendingFile && (
        <div className="panel" style={{ marginTop: 22 }}>
          <div className="mono amber" style={{ fontSize: 11, letterSpacing: 1.6 }}>FREE TIER · ONE CAR ONLY</div>
          <p className="mono" style={{ color: "var(--creamDim)", fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
            Your backup contains <span className="amber">{pendingFile.cars.length} cars</span>.
            As a free user you can import <span className="amber">one car</span> at a time.
            Pick which one to import:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
            {pendingFile.cars.map((c) => (
              <button key={c.id} className="panel" style={{ textAlign: "left", cursor: "pointer" }} onClick={() => finishImport(c.id)}>
                <span className="mono" style={{ color: "var(--cream)", fontSize: 13 }}>{c.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {hasData && !pendingFile && (
        <p className="mono" style={{ color: "var(--redline)", fontSize: 11, marginTop: 12 }}>
          Importing replaces everything currently here. Your current data is auto-downloaded first as a safety backup.
        </p>
      )}

      {err && (
        <div className="panel" style={{ marginTop: 18, borderColor: "var(--redline)" }}>
          <div className="mono" style={{ color: "var(--redline)", fontSize: 12 }}>{err}</div>
        </div>
      )}

      {report && (
        <div className="panel" style={{ marginTop: 18 }}>
          <div className="mono amber" style={{ fontSize: 11, letterSpacing: 1.6 }}>IMPORT COMPLETE</div>
          <div className="mono" style={{ color: "var(--creamDim)", fontSize: 12, marginTop: 6 }}>
            {report.skipped === 0 ? "All records imported." : `${report.skipped} record(s) skipped.`}
          </div>
          {report.reasons.slice(0, 8).map((r, i) => (
            <div key={i} className="mono muted" style={{ fontSize: 10, marginTop: 4 }}>{r}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function parseCarList(text: string): { id: string; name: string }[] {
  try {
    const root = JSON.parse(text);
    const cars = Array.isArray((root as Record<string, unknown>)["cars"]) ? (root as Record<string, unknown>)["cars"] as { id?: string; make?: string; model?: string }[] : [];
    return cars.map((c) => ({ id: c?.id ?? "", name: `${c?.make ?? ""} ${c?.model ?? ""}`.trim().toUpperCase() || "UNKNOWN" }));
  } catch { return []; }
}
